import Keycloak, { type KeycloakProfile } from 'keycloak-js'
import { writable } from 'svelte/store'

const TOKEN_MIN_VALIDITY_SECONDS = 30
const AUTH_ENABLED = readAuthEnabled()

export interface AuthState {
  enabled: boolean
  initialized: boolean
  authenticated: boolean
  token: string | null
  displayName: string | null
  username: string | null
  email: string | null
  error: string | null
}

export const authState = writable<AuthState>({
  enabled: AUTH_ENABLED,
  initialized: false,
  authenticated: false,
  token: null,
  displayName: null,
  username: null,
  email: null,
  error: null,
})

let initPromise: Promise<void> | null = null
let keycloak: Keycloak | null = null

export function initAuth(): Promise<void> {
  if (initPromise) return initPromise

  if (!AUTH_ENABLED) {
    initPromise = Promise.resolve().then(() => {
      authState.set({
        enabled: false,
        initialized: true,
        authenticated: false,
        token: null,
        displayName: null,
        username: null,
        email: null,
        error: null,
      })
    })
    return initPromise
  }

  const kc = getKeycloak()
  registerKeycloakCallbacks(kc)

  initPromise = kc
    .init({
      onLoad: 'login-required',
      pkceMethod: 'S256',
      checkLoginIframe: false,
    })
    .then(async (authenticated) => {
      if (authenticated) {
        await loadAndSyncProfile()
        return
      }

      syncAuthState(null)
    })
    .catch((error: unknown) => {
      authState.set({
        enabled: AUTH_ENABLED,
        initialized: true,
        authenticated: false,
        token: null,
        displayName: null,
        username: null,
        email: null,
        error: readErrorMessage(error),
      })
      throw error
    })

  return initPromise
}

export async function getAccessToken(
  minValiditySeconds = TOKEN_MIN_VALIDITY_SECONDS,
): Promise<string | null> {
  if (!AUTH_ENABLED) return null
  const kc = getKeycloak()
  if (!kc.didInitialize || !kc.authenticated) return null

  try {
    await kc.updateToken(minValiditySeconds)
    syncAuthState(kc.profile ?? null)
    return kc.token ?? null
  } catch {
    kc.clearToken()
    void login()
    return null
  }
}

export function login(): Promise<void> {
  if (!AUTH_ENABLED) return Promise.resolve()
  return getKeycloak().login({ redirectUri: getCurrentUrl() })
}

export function logout(): Promise<void> {
  if (!AUTH_ENABLED) return Promise.resolve()
  return getKeycloak().logout({ redirectUri: getAppUrl() })
}

function registerKeycloakCallbacks(kc: Keycloak): void {
  kc.onAuthSuccess = () => {
    void loadAndSyncProfile()
  }

  kc.onAuthRefreshSuccess = () => {
    syncAuthState(kc.profile ?? null)
  }

  kc.onAuthRefreshError = () => {
    void login()
  }

  kc.onAuthLogout = () => {
    syncAuthState(null)
  }

  kc.onTokenExpired = () => {
    void getAccessToken(TOKEN_MIN_VALIDITY_SECONDS)
  }
}

async function loadAndSyncProfile(): Promise<void> {
  const kc = getKeycloak()
  if (!kc.authenticated) {
    syncAuthState(null)
    return
  }

  try {
    const profile = await kc.loadUserProfile()
    syncAuthState(profile)
  } catch {
    syncAuthState(null)
  }
}

function syncAuthState(profile: KeycloakProfile | null): void {
  const kc = getKeycloak()
  const authenticated = kc.authenticated === true
  const firstName = readProfileValue(profile?.firstName)
  const lastName = readProfileValue(profile?.lastName)
  const profileName = [firstName, lastName].filter(Boolean).join(' ')
  const username = readProfileValue(profile?.username) ?? readClaim('preferred_username')
  const email = readProfileValue(profile?.email) ?? readClaim('email')

  authState.set({
    enabled: AUTH_ENABLED,
    initialized: kc.didInitialize,
    authenticated,
    token: authenticated ? (kc.token ?? null) : null,
    displayName: authenticated
      ? profileName || readClaim('name') || username || email || kc.subject || null
      : null,
    username: authenticated ? username : null,
    email: authenticated ? email : null,
    error: null,
  })
}

function readClaim(name: string): string | null {
  const value = keycloak?.tokenParsed?.[name]
  return typeof value === 'string' && value.trim() ? value : null
}

function readProfileValue(value: string | undefined): string | null {
  return value && value.trim() ? value : null
}

function readErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Authentication failed'
}

function readAuthEnabled(): boolean {
  const value = import.meta.env.VITE_AUTH_ENABLED
  if (value !== undefined) {
    return !['0', 'false', 'no', 'off'].includes(value.trim().toLowerCase())
  }
  return !import.meta.env.DEV
}

function getKeycloak(): Keycloak {
  keycloak ??= new Keycloak({
    url: import.meta.env.VITE_KEYCLOAK_URL || 'https://iam.og-cs.com',
    realm: import.meta.env.VITE_KEYCLOAK_REALM || 'ogcs',
    clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'argus-frontend',
  })

  return keycloak
}

function getCurrentUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined
  return window.location.href
}

function getAppUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined
  return `${window.location.origin}${window.location.pathname}`
}
